import React from 'react';

/**
 * Customizable SVG Background Component
 * @param {Object} props
 * @param {string} props.baseColor - Base color (default: gray, e.g., 'blue', 'purple', '#3498db')
 * @param {number} props.opacity - Opacity of the background (0-1)
 */
export const CustomizableSvgBackground = ({
  baseColor = 'gray',
  opacity = 1
}) => {
  // Color schemes based on base color
  const colorSchemes = {
    gray: {
      gradient1Start: '#ffffff',
      gradient1End: '#707070',
      gradient2Start: '#5d5d5d',
      gradient2End: '#545454',
      gradient3Start: '#a1a0a0',
      gradient3End: '#393939',
      primary: '#d0d0d0',
      secondary: '#c4c4c4',
      accent: '#929292',
    },
    blue: {
      gradient1Start: '#e3f2fd',
      gradient1End: '#1976d2',
      gradient2Start: '#1565c0',
      gradient2End: '#0d47a1',
      gradient3Start: '#64b5f6',
      gradient3End: '#1565c0',
      primary: '#90caf9',
      secondary: '#64b5f6',
      accent: '#42a5f5',
    },
    purple: {
      gradient1Start: '#f3e5f5',
      gradient1End: '#7b1fa2',
      gradient2Start: '#6a1b9a',
      gradient2End: '#4a148c',
      gradient3Start: '#ba68c8',
      gradient3End: '#6a1b9a',
      primary: '#ce93d8',
      secondary: '#ba68c8',
      accent: '#ab47bc',
    },
    green: {
      gradient1Start: '#e8f5e9',
      gradient1End: '#388e3c',
      gradient2Start: '#2e7d32',
      gradient2End: '#1b5e20',
      gradient3Start: '#81c784',
      gradient3End: '#2e7d32',
      primary: '#a5d6a7',
      secondary: '#81c784',
      accent: '#66bb6a',
    },
    orange: {
      gradient1Start: '#fff3e0',
      gradient1End: '#ef6c00',
      gradient2Start: '#e65100',
      gradient2End: '#bf360c',
      gradient3Start: '#ffb74d',
      gradient3End: '#e65100',
      primary: '#ffcc80',
      secondary: '#ffb74d',
      accent: '#ffa726',
    },
    red: {
      gradient1Start: '#ffebee',
      gradient1End: '#c62828',
      gradient2Start: '#b71c1c',
      gradient2End: '#8b0000',
      gradient3Start: '#e57373',
      gradient3End: '#b71c1c',
      primary: '#ef9a9a',
      secondary: '#e57373',
      accent: '#ef5350',
    },
    teal: {
      gradient1Start: '#e0f2f1',
      gradient1End: '#00897b',
      gradient2Start: '#00796b',
      gradient2End: '#004d40',
      gradient3Start: '#4db6ac',
      gradient3End: '#00796b',
      primary: '#80cbc4',
      secondary: '#4db6ac',
      accent: '#26a69a',
    },
  };

  // Get color scheme or use gray as default
  const colors = colorSchemes[baseColor.toLowerCase()] || colorSchemes.gray;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width="100%"
      height="100%"
      viewBox="0 0 1749 869"
      preserveAspectRatio="xMidYMid slice"
      style={{ opacity }}
    >
      <defs>
        <path
          id="b"
          d="M1184.977 325.484C899.46 111.884 518.28 70.143 194.473 211.91c-27.819-54.408-64.765-104-109.022-145.924C70.243 51.561 53.914 37.8 36.926 25.09 25.489 16.532 13.299 8.248 0 0v860.642h1547.53c-55.588-206.795-178.218-397.256-362.553-535.158"
        />
        <path
          id="f"
          d="M0 868.743h1270.541C1261.121 545.556 1123.348 228.316 869.466 0H0z"
        />
        <path
          id="k"
          d="M0 868.743h975.982C945.487 538.148 780.492 220.771 494.274 6.648A1075 1075 0 0 0 485.297 0H0z"
        />
        <path
          id="p"
          d="M389.215 175.315C272.06 87.67 138.795 28.968 0 0v814.776h773.901C735.24 568.442 604.185 336.141 389.215 175.315"
        />
        <path
          id="r"
          d="M550.894 346.734C409.766 169.557 213.734 49.71 0 0v412.198h598.826a999 999 0 0 0-47.932-65.46z"
        />
        <linearGradient id="a" x1="0%" x2="100%" y1="50%" y2="50%">
          <stop offset="0%" stopColor={colors.gradient1Start} />
          <stop offset="51%" stopColor={colors.primary} />
          <stop offset="62%" stopColor={colors.secondary} />
          <stop offset="76%" stopColor={colors.accent} />
          <stop offset="94%" stopColor={colors.gradient1End} />
          <stop offset="100%" stopColor={colors.gradient1End} />
        </linearGradient>
        <linearGradient id="c" x1="77.457%" x2="91.066%" y1="48.431%" y2="65.465%">
          <stop offset="0%" stopColor={colors.gradient2Start} />
          <stop offset="100%" stopColor={colors.gradient2End} />
        </linearGradient>
        <linearGradient id="g" x1="50%" x2="90.628%" y1="26.624%" y2="73.376%">
          <stop offset="0%" stopColor={colors.gradient3Start} />
          <stop offset="53.106%" stopColor={colors.accent} />
          <stop offset="100%" stopColor={colors.gradient3End} />
        </linearGradient>
        <linearGradient id="h" x1="50%" x2="90.777%" y1="26.624%" y2="73.376%">
          <stop offset="0%" stopColor={colors.accent} />
          <stop offset="53.872%" stopColor={colors.primary} />
          <stop offset="100%" stopColor={colors.secondary} />
        </linearGradient>
        <linearGradient id="l" x1="0%" x2="78.826%" y1="10.384%" y2="89.278%">
          <stop offset="0%" stopColor={colors.gradient1Start} />
          <stop offset="100%" stopColor={colors.accent} />
        </linearGradient>
        <linearGradient id="m" x1="0%" x2="79.163%" y1="10.384%" y2="89.616%">
          <stop offset="0%" stopColor={colors.accent} />
          <stop offset="50.435%" stopColor={colors.primary} />
          <stop offset="100%" stopColor={colors.accent} />
        </linearGradient>
        <linearGradient id="q" x1="4.891%" x2="82.825%" y1="15.326%" y2="100%">
          <stop offset="0%" stopColor={colors.secondary} />
          <stop offset="100%" stopColor={colors.primary} />
        </linearGradient>
        <linearGradient id="s" x1="0%" x2="77.386%" y1="37.988%" y2="73.691%">
          <stop offset="0%" stopColor={colors.primary} />
          <stop offset="100%" stopColor={colors.secondary} />
        </linearGradient>
      </defs>
      <g fill="none" fillRule="evenodd">
        <g style={{ mixBlendMode: 'overlay' }}>
          <path fill="url(#a)" fillRule="nonzero" d="M0 0h1749v868.829H0z" />
          <g transform="translate(0 8.1)">
            <use xlinkHref="#b" fill="url(#c)" />
          </g>
          <use xlinkHref="#f" fill="url(#g)" />
          <use xlinkHref="#f" fill="url(#h)" />
          <use xlinkHref="#k" fill="url(#l)" />
          <use xlinkHref="#k" fill="url(#m)" />
          <use xlinkHref="#p" fill="url(#q)" transform="translate(0 53.967)" />
          <use xlinkHref="#r" fill="url(#s)" transform="translate(0 456.548)" />
        </g>
      </g>
    </svg>
  );
};
